<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: { type: [String, Number], default: '' },
    selectedLabel: { type: String, default: '' },
    searchUrl: { type: String, default: '' },
    mode: { type: String, default: 'number' },
    minimumInputLength: { type: Number, default: 3 },
    debounceMs: { type: Number, default: 250 },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    labels: { type: Object, default: () => ({}) },
    initialQuery: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue', 'update:selectedLabel', 'select', 'clear']);

const rootEl = ref(null);
const inputEl = ref(null);
const query = ref(props.initialQuery || '');
const open = ref(false);
const loading = ref(false);
const results = ref([]);
const highlightIndex = ref(-1);
let debounceTimer = null;
let abortController = null;

const selectedText = computed(() => props.selectedLabel || '');
const showDropdown = computed(() => open.value && !props.disabled);

const statusMessage = computed(() => {
    const needle = query.value.trim();
    if (needle.length < props.minimumInputLength) {
        return props.labels.orderSearchMinChars || `Digita almeno ${props.minimumInputLength} caratteri`;
    }
    if (loading.value) {
        return props.labels.loading || '…';
    }
    if (!results.value.length) {
        return props.labels.orderSearchEmpty || 'Nessun risultato';
    }
    return '';
});

watch(() => props.initialQuery, (value) => {
    if (!props.modelValue && typeof value === 'string') {
        query.value = value;
    }
});

const clearTimer = () => {
    if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
    }
};

const abortFetch = () => {
    if (abortController) {
        abortController.abort();
        abortController = null;
    }
};

const fetchResults = async (needle) => {
    if (!props.searchUrl || needle.length < props.minimumInputLength) {
        results.value = [];
        loading.value = false;
        return;
    }

    abortFetch();
    abortController = new AbortController();
    loading.value = true;

    try {
        const url = new URL(props.searchUrl, window.location.origin);
        url.searchParams.set('mode', props.mode);
        url.searchParams.set('needle', needle);

        const response = await fetch(url.toString(), {
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            signal: abortController.signal,
        });
        const data = await response.json().catch(() => []);
        if (!response.ok) {
            results.value = [];
            return;
        }
        results.value = Array.isArray(data) ? data : [];
        highlightIndex.value = results.value.length ? 0 : -1;
    } catch (error) {
        if (error?.name !== 'AbortError') {
            results.value = [];
        }
    } finally {
        loading.value = false;
    }
};

const scheduleFetch = () => {
    clearTimer();
    const needle = query.value.trim();
    if (needle.length < props.minimumInputLength) {
        results.value = [];
        loading.value = false;
        return;
    }
    debounceTimer = setTimeout(() => {
        fetchResults(needle);
    }, props.debounceMs);
};

const openDropdown = () => {
    if (props.disabled) {
        return;
    }
    open.value = true;
    scheduleFetch();
};

const closeDropdown = () => {
    open.value = false;
    highlightIndex.value = -1;
};

const clearSelection = () => {
    emit('update:modelValue', '');
    emit('update:selectedLabel', '');
    emit('clear');
    query.value = props.initialQuery || '';
    results.value = [];
    nextTick(() => inputEl.value?.focus());
};

const chooseResult = (item) => {
    if (!item) {
        return;
    }
    emit('update:modelValue', item.id);
    emit('update:selectedLabel', item.text || String(item.id));
    emit('select', item);
    query.value = item.text || String(item.id);
    closeDropdown();
};

const onInput = () => {
    if (props.modelValue !== '' && props.modelValue !== null && props.modelValue !== undefined) {
        emit('update:modelValue', '');
        emit('update:selectedLabel', '');
    }
    open.value = true;
    scheduleFetch();
};

const onKeydown = (event) => {
    if (!showDropdown.value) {
        if (event.key === 'ArrowDown') {
            openDropdown();
        }
        return;
    }

    if (event.key === 'Escape') {
        event.preventDefault();
        closeDropdown();
        return;
    }

    if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!results.value.length) {
            return;
        }
        highlightIndex.value = (highlightIndex.value + 1) % results.value.length;
        return;
    }

    if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!results.value.length) {
            return;
        }
        highlightIndex.value = highlightIndex.value <= 0
            ? results.value.length - 1
            : highlightIndex.value - 1;
        return;
    }

    if (event.key === 'Enter') {
        event.preventDefault();
        if (highlightIndex.value >= 0 && results.value[highlightIndex.value]) {
            chooseResult(results.value[highlightIndex.value]);
        }
    }
};

const onDocumentPointerDown = (event) => {
    if (!rootEl.value?.contains(event.target)) {
        closeDropdown();
    }
};

onMounted(() => {
    document.addEventListener('pointerdown', onDocumentPointerDown);
});

onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', onDocumentPointerDown);
    clearTimer();
    abortFetch();
});
</script>

<template>
    <div ref="rootEl" class="ticket-desk-ajax-select" :class="{ 'is-open': showDropdown, 'is-disabled': disabled }">
        <div class="ticket-desk-ajax-select__control">
            <input
                ref="inputEl"
                v-model="query"
                type="search"
                autocomplete="off"
                data-testid="ticket-desk-order-search-input"
                :placeholder="placeholder"
                :disabled="disabled"
                :aria-expanded="showDropdown"
                aria-autocomplete="list"
                role="combobox"
                @focus="openDropdown"
                @input="onInput"
                @keydown="onKeydown"
            />
            <button
                v-if="modelValue !== '' && modelValue !== null && modelValue !== undefined"
                type="button"
                class="ticket-desk-ajax-select__clear"
                :aria-label="labels.clear || 'Clear'"
                :disabled="disabled"
                @click="clearSelection"
            >
                ×
            </button>
        </div>
        <ul
            v-if="showDropdown"
            class="ticket-desk-ajax-select__dropdown"
            role="listbox"
        >
            <li v-if="statusMessage" class="ticket-desk-ajax-select__status">{{ statusMessage }}</li>
            <li
                v-for="(item, index) in results"
                :key="`${item.id}-${index}`"
                class="ticket-desk-ajax-select__option"
                :class="{ 'is-active': index === highlightIndex }"
                role="option"
                :aria-selected="index === highlightIndex"
                @mousedown.prevent="chooseResult(item)"
                @mouseenter="highlightIndex = index"
            >
                {{ item.text || item.id }}
            </li>
        </ul>
        <p v-if="selectedText && modelValue !== ''" class="ticket-desk-ajax-select__chosen">
            {{ selectedText }}
        </p>
    </div>
</template>
